import pluralize from 'pluralize';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { ConfirmationModal } from '@air/component-confirmation-modal';
import { DateInput } from '@air/primitive-date-input';
import { parseAirDateToISO } from '@air/utilities';

import { type BulkEditCustomFieldModalProps } from './types';

export type BulkEditDateValueCustomFieldModalProps = {
  onSave: (params: {
    assetIds: string[];
    boardIds: string[];
    baseCustomField: BulkEditCustomFieldModalProps['field'];
    nextDateValue: string | undefined;
  }) => Promise<void>;
} & BulkEditCustomFieldModalProps;

export const BulkEditDateValueCustomFieldModal = memo(
  ({
    field,
    assetIds,
    boardIds,
    initialValues,
    initialStatus,
    totalSelectedItemsCount,
    isLoadingInitialValues = false,
    onClose,
    onSave,
  }: AirModalProps<BulkEditDateValueCustomFieldModalProps>) => {
    const selectedItemsCount = totalSelectedItemsCount ?? assetIds.length + boardIds.length;
    const allHaveSameValue = useMemo(() => {
      if (initialStatus === 'mixed') return false;
      const baseValue = initialValues[0]?.dateValue;
      return initialValues.every((cf) => cf.dateValue === baseValue);
    }, [initialStatus, initialValues]);

    const originalValue = allHaveSameValue ? initialValues[0]?.dateValue : undefined;
    const originalDateValue = originalValue ? parseAirDateToISO(originalValue) : undefined;
    const [dateValue, setDateValue] = useState<Date | null | undefined>(originalDateValue);
    const [isTouched, setIsTouched] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
      if (!isTouched) {
        setDateValue(originalDateValue);
      }
    }, [isTouched, originalDateValue]);

    const onConfirm = useCallback(async () => {
      setIsSubmitting(true);
      const nextDateValue = dateValue?.toISOString();

      await onSave({
        assetIds,
        boardIds,
        baseCustomField: field,
        nextDateValue,
      });
      onClose();
    }, [assetIds, boardIds, dateValue, field, onClose, onSave]);

    return (
      <ConfirmationModal
        id="bulk-edit-date-value-custom-field-modal"
        isOpen
        title={`Edit ${field.name}`}
        description={`Select a date to apply to the ${pluralize('item', selectedItemsCount, true)} you've selected.`}
        ctaPrimaryText="Save"
        onConfirm={onConfirm}
        isLoading={isSubmitting || isLoadingInitialValues}
        isDisabled={dateValue === originalDateValue || isSubmitting || isLoadingInitialValues}
        onDismiss={onClose}
      >
        <div className="mt-4">
          <DateInput
            size="large"
            selected={dateValue}
            placeholderText="Add a date"
            onChange={(date: Date | [Date, Date] | null) => {
              if (isLoadingInitialValues) return;
              if (Array.isArray(date)) return;
              setIsTouched(true);
              setDateValue(date);
            }}
            id={field.id}
            name={field.name}
            className="w-full"
            disabled={isLoadingInitialValues}
          />
        </div>
      </ConfirmationModal>
    );
  },
);
BulkEditDateValueCustomFieldModal.displayName = 'BulkEditDateValueCustomFieldModal';
